/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ProductList } from '@msdyn365-commerce/retail-proxy';
import { Button } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import {
    IOrderTemplateNameDialogResources,
    OrderTemplateNameDialog
} from '../order-template-name-dialog/order-template-name-dialog.component';

export interface IRenameOrderTemplateComponentProps extends IComponentProps<IRenameOrderTemplateData> {
    className?: string;
    renameOrderTemplateDialogStrings: IOrderTemplateNameDialogResources;
    renameOrderTemplateButtonText: string;
    onRenameTemplate(orderTemplate: ProductList): Promise<void>;

    // as required passing in components for later user
    isMultipleCatalogFeatureEnabledInHq?: boolean;
    catalogId?: number;
}

interface IRenameOrderTemplateData {
    orderTemplate: ProductList;
}

export interface IRenameOrderTemplateComponent extends IComponent<IRenameOrderTemplateComponentProps> {}

// eslint-disable-next-line @typescript-eslint/naming-convention -- For backward compatibility
const RenameOrderTemplateComponentActions = {};
let buttonEventReference: HTMLButtonElement | undefined;

const RenameOrderTemplate: React.FC<IRenameOrderTemplateComponentProps> = (props: IRenameOrderTemplateComponentProps) => {
    const {
        data: { orderTemplate },
        renameOrderTemplateButtonText,
        renameOrderTemplateDialogStrings,
        context
    } = props;
    const [renameOrderTemplateDialogOpen, setOrderTemplateNameDialogOpen] = React.useState(false);
    const onOrderTemplateNameCancel = () => {
        context.telemetry.debug('Rename of template was canceled.');
        buttonEventReference?.focus();
    };

    const onRenameButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        buttonEventReference = event.target as HTMLButtonElement;
        setOrderTemplateNameDialogOpen(true);
    };
    return (
        <>
            <Button className='ms-order-template-action-bar__rename-template-button' onClick={onRenameButtonClick}>
                {renameOrderTemplateButtonText}
            </Button>
            <OrderTemplateNameDialog
                existingOrderTemplate={orderTemplate}
                isUpdateDialog
                className='msc-order-template-name-dialog'
                modalOpen={renameOrderTemplateDialogOpen}
                setModalOpen={setOrderTemplateNameDialogOpen}
                context={context}
                id={props.id}
                data={props.data}
                orderTemplateName={props.data.orderTemplate.Name}
                typeName={props.typeName}
                dialogStrings={renameOrderTemplateDialogStrings}
                onCancel={onOrderTemplateNameCancel}
                onSuccess={props.onRenameTemplate}
            />
        </>
    );
};

export const RenameOrderTemplateComponent: React.FunctionComponent<IRenameOrderTemplateComponentProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error
    IRenameOrderTemplateComponent
>('RenameOrderTemplate', { component: RenameOrderTemplate, ...RenameOrderTemplateComponentActions });
